import React, {useEffect, useState, useContext} from "react";
import DashboardWidget from "./DashboardWidget";
import axios from "../common/axios";
import {ContentContext} from "../common/ContentContext";
import {UserContext} from "../common/UserContext";
import useCurrentConcept from "../hooks/useCurrentConcept";
import Button from "../common/Button";
import {ArrowBackRounded, ArrowForwardRounded} from "@mui/icons-material";
import {Divider} from "antd";
import {useScreenMode} from "../common/context/ScreenModeContext";

const MyCourseLessonNavigationWidget = props => {
    const {...other} = props;
    const {content} = useContext(ContentContext);
    const {user} = useContext(UserContext);
    const {loadCurrentConcept} = useCurrentConcept();
    const [isLoading, setLoading] = useState(true);
    const [lastConcept, setLastConcept] = useState();
    const [nextConcept, setNextConcept] = useState();
    const [coverImageError, setCoverImageError] = useState(false);
    const { mobileScreenMode } = useScreenMode();

    const loadLastConcept = () => {
        if(content.currentConcept == undefined || content.currentConcept.conceptId == undefined) {
            setLoading(false);
            return;
        }

        setLoading(true);
        axios.get('/web/ui/content/concept/' + content.currentConcept.conceptId)
            .then(response => {
                setLastConcept(response.data);
                setLoading(false);
            });
    }

    const loadNextConcept = () => {
        if(content.currentConcept?.nextConceptId){
            setLoading(true)
            axios.get('/web/ui/content/concept/' + content.currentConcept.nextConceptId)
                .then(response => {
                    setNextConcept(response.data);
                    setLoading(false);
                });
        } else if(user.syllabusId) {
            setLoading(true);
            axios.get('/web/ui/content/syllabus/' + user.syllabusId + '/firstConcept')
                .then(response => {
                    setNextConcept(response.data);
                    setLoading(false);
                });
        } else {
            setNextConcept(undefined);
            setLoading(false);
        }
    }

    const handleLoadConcept = conceptId => {
        loadCurrentConcept(conceptId);
    }

    useEffect(() => {
        loadLastConcept();
        loadNextConcept();
    }, []);

    const getBookCoverHTML = () => {
        return (
            <div className="ch-lesson-nav-widget-image-wrapper">
                {!coverImageError && content.currentSyllabus.bookCoverUrl
                    ? <img
                        className="ch-lesson-nav-widget-image"
                        alt={`${content.currentSyllabus.syllabusName} textbook cover`}
                        src={content.currentSyllabus.bookCoverUrl}
                        onError={() => setCoverImageError(true)}
                    />
                    : <div className={'ch-lesson-nav-widget-image empty'} aria-hidden={true}>
                        <TextBookPlaceholder />
                        <p>{content.currentSyllabus.syllabusName}</p>
                    </div>
                }
            </div>
        )
    }

    if(!content.currentSyllabus){
        return null;
    }

    return (
        <>
            <h2>{lastConcept ? `My resources` : `Get started`}</h2>
            <DashboardWidget
                isLoading={isLoading}
                {...other}>
                <div className="ch-lesson-nav-widget-wrapper">
                    {!mobileScreenMode && getBookCoverHTML()}
                    <div className={'ch-lesson-nav-widget-content-wrapper'}>
                        <div className={'ch-lesson-nav-widget-book-title-wrapper'}>
                            {mobileScreenMode && getBookCoverHTML()}
                            {content.currentSyllabus &&
                                <h3 className="ch-lesson-nav-widget-book-title">{content.currentSyllabus.syllabusName}</h3>
                            }
                        </div>
                        <Divider className="ch-divider"/>
                        <div className="ch-lesson-nav-widget-section-container">
                            {lastConcept &&
                                <div className="ch-lesson-nav-widget-section-item">
                                    <>
                                        <p className="ch-lesson-nav-widget-section-heading">Last Viewed</p>
                                        <Button
                                            type={'outline'}
                                            onClick={() => handleLoadConcept(lastConcept.conceptId)}
                                            className={'ch-lesson-nav-widget-section-button'}
                                            data-gtm-value={'dashboard-nav-last'}
                                        >
                                                <span className={'ch-lesson-nav-widget-section-button-content'}>
                                                    <span>
                                                        {lastConcept.topicName}
                                                    </span>
                                                    <span>
                                                        {lastConcept.conceptName}
                                                    </span>
                                                    <ArrowBackRounded/>
                                                </span>
                                        </Button>
                                    </>
                                </div>
                            }
                            <div className="ch-lesson-nav-widget-section-item">
                                <p className="ch-lesson-nav-widget-section-heading">Next section</p>
                                {nextConcept &&
                                    <Button
                                        type={'outline'}
                                        onClick={() => handleLoadConcept(nextConcept.conceptId)}
                                        className={'ch-lesson-nav-widget-section-button'}
                                        data-gtm-value={'dashboard-nav-next'}
                                    >
                                        <span className={'ch-lesson-nav-widget-section-button-content'}>
                                            <span>
                                                {nextConcept.topicName}
                                            </span>
                                            <span>
                                                {nextConcept.conceptName}
                                            </span>
                                            <ArrowForwardRounded style={{alignSelf: "flex-end"}}/>
                                        </span>
                                    </Button>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardWidget>
        </>
    )
}

export default MyCourseLessonNavigationWidget;

const TextBookPlaceholder = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={175}
        height={220}
        viewBox="0 0 675 900"
        className={'.ch-lesson-nav-widget-image empty'}
    >
        {/* Background */}
        <rect width="675" height="900" fill="var(--global-menu-colour)" />

        <g>
            <circle cx={381} cy={155} r={156} fill="var(--primary-colour)" />
            <circle cx={51} cy={415} r={20} fill="var(--primary-colour)" />
            <circle cx={531} cy={492} r={67} fill="var(--secondary-colour)" />
            <circle cx={523} cy={840} r={55} fill="var(--secondary-colour)" />
            <circle cx={13} cy={62} r={106} fill="var(--secondary-colour)" />
            <circle cx={210} cy={737} r={154} fill="var(--primary-colour)" />
        </g>
    </svg>
)
